import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Users, Plus, Search, MoreHorizontal, Pencil, Trash2, Phone, Mail, MapPin, FileText, Receipt, Wrench, Eye, Building, ExternalLink, Upload, Download, Loader2, CheckCircle2, ArrowUp, ArrowDown, ArrowUpDown, Clock, ArrowUpAZ } from 'lucide-react';
import PhoneLink from '@/components/voice/PhoneLink';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import ManageAddresses from '@/components/common/ManageAddresses';
import { Pagination } from '@/components/common/Pagination';
import { toTitleCase } from '@/lib/capitalize';
import { useCounties } from '@/hooks/useCounties';
import { useAuth } from '@/context/AuthContext';
import ContactNameStack from '@/components/common/ContactNameStack';
import { contactDisplayName, isCommercialContact } from '@/lib/contactDisplay';
import { US_STATES, stateNameToCode } from '@/constants/usStates';

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limited = digits.slice(0, 10);
  
  // Format based on length
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

// Fix S (2026-09-14): additional_phones/emails pueden traer {name,phone,label}
// ademas del string legacy — nunca reescribir el dato, solo leerlo seguro.
const phoneEntryOf = (p) => (typeof p === 'string' ? { name: '', phone: p, label: '' } : { name: p?.name || '', phone: p?.phone || '', label: p?.label || '' });
const emailEntryOf = (e) => (typeof e === 'string' ? { name: '', email: e } : { name: e?.name || '', email: e?.email || '' });

// Main contact types for filtering and selection
const CONTACT_TYPES = ['customer', 'vendor', 'subcontractor'];
const TYPE_FILTERS = ['all', 'customer', 'vendor', 'subcontractor'];
const ACCOUNT_TYPES = ['residential', 'commercial', 'subcontractor', 'vendor', 'other'];
const ACCOUNT_TYPE_CONFIG = {
  residential: { label: 'Residential', type: 'customer', is_customer: true },
  commercial: { label: 'Commercial', type: 'customer', is_customer: true },
  subcontractor: { label: 'Subcontractor', type: 'subcontractor', is_customer: false },
  vendor: { label: 'Vendor', type: 'vendor', is_customer: false },
  other: { label: 'Other', type: 'customer', is_customer: true },
};
const COUNTIES = null; // Replaced by useCounties hook

export default function ContactsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const counties = useCounties();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('recents');
  const [sortDir, setSortDir] = useState('desc');

  // Manage Addresses
  const [manageAddrOpen, setManageAddrOpen] = useState(false);
  const [manageAddrContact, setManageAddrContact] = useState(null);
  const pageSize = 25;
  const [form, setForm] = useState({
    first_name: '', last_name: '', company_name: '', email: '', phone: '',
    additional_phones: [], additional_emails: [],
    // Fix F (2026-08-31): personas adicionales dentro de un contacto comercial
    // (property managers con 3-4 personas: el que paga, accountant, PM). Ningun
    // campo interno obligatorio. Solo se usa cuando account_type === 'commercial'.
    contact_people: [],
    type: 'customer', account_type: 'residential',
    address: '', city: '', county: '', state: 'FL', zip: '',
    has_drilling_license: false, license_number: '', notes: '', is_customer: true,
    subcontractor_category: '', payment_type: '', commission_type: ''
  });

  // Simplified labels for contact types
  const TYPE_LABELS = { 
    all: t('contacts.filters.all'), 
    customer: t('contacts.filters.customers'),
    vendor: t('contacts.filters.vendors'),
    subcontractor: t('contacts.subcontractor')
  };
  
  // Account type labels (for customers only)
  const ACCOUNT_TYPE_LABELS = {
    residential: t('contacts.accountTypes.residential'),
    commercial: t('contacts.accountTypes.commercial')
  };

  const fetchData = useCallback(async () => {
    try {
      const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (search) params.search = search;
      const res = await api.get('/contacts', { params });
      const data = res.data;
      setContacts(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch { toast.error(t('errors.loadingError')); }
    finally { setLoading(false); }
  }, [typeFilter, search, page, sortBy, sortDir, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => { setPage(1); }, [search, typeFilter, sortBy, sortDir]);

  // Toggle a sortable column header: same column flips direction, new column starts asc.
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 opacity-30 inline ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 inline ml-1 text-[#3399FF]" />
      : <ArrowDown className="w-3 h-3 inline ml-1 text-[#3399FF]" />;
  };

  // Auto-open create form when navigated from Maps with pre-filled address
  useEffect(() => {
    if (location.state?.fromMap) {
      setEditing(null);
      setForm(prev => ({
        ...prev,
        first_name: location.state.firstName || '', last_name: location.state.lastName || '', company_name: '', email: '', phone: '',
        additional_phones: [], additional_emails: [], contact_people: [],
        type: 'customer', account_type: 'residential',
        address: location.state.address || '',
        city: location.state.city || '',
        county: location.state.county || '',
        state: location.state.stateVal || 'FL',
        zip: location.state.zip || '',
        has_drilling_license: false, license_number: '', notes: location.state.parcelId ? `Parcela: ${location.state.parcelId}` : '', is_customer: true,
      }));
      setDialogOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setForm({
      first_name: '', last_name: '', company_name: '', email: '', phone: '',
      additional_phones: [], additional_emails: [], contact_people: [],
      type: 'customer', account_type: 'residential',
      address: '', city: '', county: '', state: 'FL', zip: '',
      has_drilling_license: false, license_number: '', notes: '', is_customer: true
    });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    // Map account_type for backward compatibility
    let accountType = c.account_type || 'residential';
    if (!ACCOUNT_TYPE_CONFIG[accountType]) {
      // Old contacts without account_type: derive from type
      if (c.type === 'vendor') accountType = 'vendor';
      else if (c.type === 'subcontractor') accountType = 'subcontractor';
      else if (c.company_name) accountType = 'commercial';
      else accountType = 'residential';
    }
    const cfg = ACCOUNT_TYPE_CONFIG[accountType];
    
    setForm({
      first_name: c.first_name || '', last_name: c.last_name || '', company_name: c.company_name || '',
      email: c.email || '', phone: c.phone || '',
      additional_phones: c.additional_phones || [],
      additional_emails: c.additional_emails || [],
      contact_people: c.contact_people || [],
      type: cfg.type,
      account_type: accountType,
      address: c.address || '', city: c.city || '', county: c.county || '',
      state: stateNameToCode(c.state) || 'FL', zip: c.zip || '',
      has_drilling_license: c.has_drilling_license || false, license_number: c.license_number || '',
      notes: c.notes || '', is_customer: cfg.is_customer
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error(t('errors.requiredField')); return; }
    try {
      if (editing) {
        await api.put(`/contacts/${editing.id}`, form);
        toast.success(t('success.updated'));
      } else {
        await api.post('/contacts', form);
        toast.success(t('success.created'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  // Fix F (2026-08-31): un solo "+" agrega el cluster completo (nombre, apellido,
  // teléfono, etiqueta, email) — ningún campo interno es obligatorio.
  const addContactPerson = () => {
    setForm(prev => ({
      ...prev,
      contact_people: [...(prev.contact_people || []), { id: crypto.randomUUID(), first_name: '', last_name: '', phone: '', phone_label: '', email: '', notes: '' }],
    }));
  };
  const updateContactPerson = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      contact_people: prev.contact_people.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    }));
  };
  const removeContactPerson = (idx) => {
    setForm(prev => ({ ...prev, contact_people: prev.contact_people.filter((_, i) => i !== idx) }));
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/contacts/${id}`); toast.success(t('success.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/contacts/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      toast.success(t('contacts.importDone', { imported: res.data.imported, updated: res.data.updated }));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/contacts/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('errors.savingError'));
    } finally {
      setExporting(false);
    }
  };

  const openDetail = (contact) => {
    // Navigate to the full contact profile page
    navigate(`/contacts/${contact.id}`);
  };

  const createEstimate = (contact) => {
    setDetailDialogOpen(false);
    navigate('/estimates', { state: { createFor: contact.id, contactName: `${contact.first_name} ${contact.last_name}` } });
  };

  const createInvoice = (contact) => {
    setDetailDialogOpen(false);
    navigate('/invoices', { state: { createFor: contact.id, contactName: `${contact.first_name} ${contact.last_name}` } });
  };

  const createJob = (contact) => {
    setDetailDialogOpen(false);
    navigate('/jobs', { state: { createFor: contact.id, contactName: `${contact.first_name} ${contact.last_name}` } });
  };

  return (
    <div data-testid="contacts-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('contacts.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('contacts.totalContacts', { count: contacts.length })} (Rolodex)</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {isAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleImportFile}
                data-testid="import-contacts-input"
              />
              <Button
                data-testid="import-contacts-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                size="sm"
                variant="outline"
                className="border-gray-200"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {t('contacts.import')}
              </Button>
              <Button
                data-testid="export-contacts-btn"
                onClick={handleExport}
                disabled={exporting}
                size="sm"
                variant="outline"
                className="border-gray-200"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {t('contacts.export')}
              </Button>
            </>
          )}
          <Button data-testid="new-contact-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm">
            <Plus className="w-4 h-4 mr-2" /> {t('contacts.newContact')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-contacts" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('contacts.searchPlaceholder')} className="pl-10 h-9 bg-white" />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="clear-search-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>
        <div className="tabs-scroll">
          <Tabs value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }} className="w-auto">
            <TabsList className="bg-white border border-gray-200 h-9">
              {TYPE_FILTERS.slice(0, 5).map(tf => (
                <TabsTrigger key={tf} value={tf} data-testid={`contact-filter-${tf}`} className="text-xs px-2 sm:px-2.5 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">
                  {TYPE_LABELS[tf]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        {/* Alphabetical / Recents view toggle */}
        <div className="inline-flex rounded-md border border-gray-200 bg-white h-9 overflow-hidden">
          <button
            data-testid="contacts-view-alphabetical"
            onClick={() => { setSortBy('name'); setSortDir('asc'); }}
            className={`px-3 text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${sortBy !== 'recents' ? 'bg-[#003366] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            <ArrowUpAZ className="w-3.5 h-3.5" /> {t('contacts.alphabetical')}
          </button>
          <button
            data-testid="contacts-view-recents"
            onClick={() => setSortBy('recents')}
            className={`px-3 text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 border-l border-gray-200 ${sortBy === 'recents' ? 'bg-[#003366] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            <Clock className="w-3.5 h-3.5" /> {t('contacts.recents')}
          </button>
        </div>
      </div>

      {/* Desktop Table */}
      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : contacts.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('contacts.noContacts')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('contacts.createFirstContact')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th onClick={() => handleSort('name')} data-testid="sort-header-name" className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 cursor-pointer select-none hover:text-[#003366]">{t('common.name')}<SortIcon col="name" /></th>
                    <th onClick={() => handleSort('email')} data-testid="sort-header-email" className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 cursor-pointer select-none hover:text-[#003366]">{t('common.email')}<SortIcon col="email" /></th>
                    <th onClick={() => handleSort('phone')} data-testid="sort-header-phone" className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 cursor-pointer select-none hover:text-[#003366]">{t('common.phone')}<SortIcon col="phone" /></th>
                    <th onClick={() => handleSort('city')} data-testid="sort-header-address" className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 cursor-pointer select-none hover:text-[#003366]">{t('common.address')}<SortIcon col="city" /></th>
                    <th onClick={() => handleSort('type')} data-testid="sort-header-type" className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden lg:table-cell cursor-pointer select-none hover:text-[#003366]">{t('common.type')}<SortIcon col="type" /></th>
                    <th onClick={() => handleSort('county')} data-testid="sort-header-county" className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden lg:table-cell cursor-pointer select-none hover:text-[#003366]">{t('common.county')}<SortIcon col="county" /></th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} data-testid={`contact-row-${c.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => openDetail(c)}>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {isCommercialContact(c) ? <Building className="w-4 h-4" /> : `${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}`}
                          </div>
                          <ContactNameStack contact={c} testId={`contact-name-${c.id}`} />
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        {c.email ? (
                          <div className="space-y-0.5">
                            <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} className="text-sm text-blue-600 hover:text-blue-800 hover:underline block">
                              {c.email}
                            </a>
                            {(c.additional_emails || []).map((raw, i) => {
                              const ee = emailEntryOf(raw);
                              return (
                                <a key={i} href={`mailto:${ee.email}`} onClick={e => e.stopPropagation()} className="text-xs text-blue-500 hover:underline block">
                                  {ee.email}{ee.name ? ` · ${ee.name}` : ''}
                                </a>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {c.phone ? (
                          <div className="space-y-0.5">
                            <PhoneLink
                              number={c.phone}
                              name={[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name}
                              testId={`contact-phone-link-${c.id}`}
                              className="text-sm !text-gray-700 hover:!text-[#003366] gap-1.5"
                            />
                            {(c.additional_phones || []).map((raw, i) => {
                              const pe = phoneEntryOf(raw);
                              const tag = [pe.name, pe.label].filter(Boolean).join(' · ');
                              return (
                                <div key={i} className="flex items-center gap-1">
                                  <PhoneLink
                                    number={pe.phone}
                                    name={[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name}
                                    testId={`contact-phone-link-${c.id}-${i + 1}`}
                                    showIcon={false}
                                    className="text-xs !text-gray-600 hover:!text-[#003366] gap-1.5 ml-5"
                                  />
                                  {tag && <span className="text-[11px] text-gray-400">{tag}</span>}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {(c.address || c.city) ? (
                          <div className="text-sm text-gray-600">
                            <p className="truncate max-w-[200px]">{c.address || ''}</p>
                            {c.city && <p className="text-xs text-gray-400">{c.city}{c.state ? `, ${c.state}` : ''} {c.zip || ''}</p>}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 hidden lg:table-cell">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600 capitalize">
                          {ACCOUNT_TYPE_CONFIG[c.account_type]?.label || TYPE_LABELS[c.type] || c.type}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 hidden lg:table-cell">
                        {c.county ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="w-3 h-3" />{c.county}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`contact-actions-${c.id}`} className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetail(c)}><Eye className="w-3.5 h-3.5 mr-2" />{t('contacts.viewProfile')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setManageAddrContact(c); setManageAddrOpen(true); }} data-testid={`manage-addresses-${c.id}`}>
                              <MapPin className="w-3.5 h-3.5 mr-2 text-[#3399FF]" />Manage Addresses
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => createEstimate(c)}><FileText className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('contacts.createEstimate')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => createInvoice(c)}><Receipt className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('contacts.createInvoice')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => createJob(c)}><Wrench className="w-3.5 h-3.5 mr-2 text-amber-500" />{t('jobs.newJob')}</DropdownMenuItem>
                            {c.phone && <DropdownMenuItem><Phone className="w-3.5 h-3.5 mr-2" />{t('common.phone')}</DropdownMenuItem>}
                            {c.email && <DropdownMenuItem><Mail className="w-3.5 h-3.5 mr-2" />{t('common.email')}</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : contacts.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('contacts.noContacts')}</p>
          </CardContent></Card>
        ) : (
          contacts.map((c, idx) => (
            <Card key={c.id} data-testid={`contact-mobile-${c.id}`} className="bg-white border-gray-100 shadow-sm mobile-card-enter cursor-pointer" style={{ animationDelay: `${idx * 50}ms` }} onClick={() => openDetail(c)}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {isCommercialContact(c) ? <Building className="w-4 h-4" /> : `${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {contactDisplayName(c).primary}
                      </p>
                      <div onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 -mr-2"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetail(c)}><Eye className="w-3.5 h-3.5 mr-2" />{t('contacts.viewProfile')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setManageAddrContact(c); setManageAddrOpen(true); }}>
                              <MapPin className="w-3.5 h-3.5 mr-2 text-[#3399FF]" />Manage Addresses
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => createEstimate(c)}><FileText className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('contacts.createEstimate')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => createInvoice(c)}><Receipt className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('contacts.createInvoice')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => createJob(c)}><Wrench className="w-3.5 h-3.5 mr-2 text-amber-500" />{t('jobs.newJob')}</DropdownMenuItem>
                            {c.phone && <DropdownMenuItem><Phone className="w-3.5 h-3.5 mr-2" />{t('common.phone')}</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-600 capitalize">
                        {TYPE_LABELS[c.type] || c.type}
                      </span>
                      {contactDisplayName(c).secondary && <span className="text-xs text-gray-400">{contactDisplayName(c).secondary}</span>}
                    </div>
                    {/* Email - Prominent */}
                    {c.email && (
                      <div className="mt-2 space-y-0.5">
                        <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <Mail className="w-3 h-3" />{c.email}
                        </a>
                        {(c.additional_emails || []).map((raw, i) => {
                          const ee = emailEntryOf(raw);
                          return (
                            <a key={i} href={`mailto:${ee.email}`} onClick={e => e.stopPropagation()} className="text-xs text-blue-500 hover:underline flex items-center gap-1 ml-4">
                              {ee.email}{ee.name ? ` · ${ee.name}` : ''}
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {/* Phone and Address */}
                    <div className="flex flex-col gap-1 mt-1.5 text-xs text-gray-500">
                      {c.phone && (
                        <>
                          <PhoneLink
                            number={c.phone}
                            name={[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name}
                            testId={`contact-card-phone-link-${c.id}`}
                            className="flex"
                          />
                          {(c.additional_phones || []).map((raw, i) => {
                            const pe = phoneEntryOf(raw);
                            const tag = [pe.name, pe.label].filter(Boolean).join(' · ');
                            return (
                              <div key={i} className="flex items-center gap-1">
                                <PhoneLink
                                  number={pe.phone}
                                  name={[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name}
                                  testId={`contact-card-phone-link-${c.id}-${i + 1}`}
                                  showIcon={false}
                                  className="flex ml-4"
                                />
                                {tag && <span className="text-[11px] text-gray-400">{tag}</span>}
                              </div>
                            );
                          })}
                        </>
                      )}
                      {(c.address || c.city) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{[c.address, c.city, c.county].filter(Boolean).join(', ')}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('contacts.editContact') : t('contacts.newContact')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5">
            
            {/* Account Type - Single Selection */}
            <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl border border-blue-100">
              <Label className="text-sm font-semibold text-gray-700">{t('contacts.accountType')} *</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {ACCOUNT_TYPES.map(at => {
                  const cfg = ACCOUNT_TYPE_CONFIG[at];
                  const isSelected = form.account_type === at;
                  return (
                    <button
                      key={at}
                      type="button"
                      data-testid={`account-type-${at}`}
                      onClick={() => setForm({...form, account_type: at, type: cfg.type, is_customer: cfg.is_customer})}
                      className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                        isSelected
                          ? 'border-[#003366] bg-[#003366] text-white shadow-lg' 
                          : 'border-gray-200 bg-white hover:border-[#3399FF] hover:bg-blue-50'
                      }`}
                    >
                      {(at === 'residential' || at === 'other') && <Users className="w-5 h-5" />}
                      {at === 'commercial' && <Building className="w-5 h-5" />}
                      {at === 'subcontractor' && <Wrench className="w-5 h-5" />}
                      {at === 'vendor' && <Building className="w-5 h-5" />}
                      <span className="text-xs font-medium">{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* CONDITIONAL FIELDS BASED ON ACCOUNT TYPE */}
            <div className="space-y-4">
              
              {/* Company Name for commercial/vendor/subcontractor */}
              {['commercial', 'subcontractor', 'vendor'].includes(form.account_type) ? (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    {t('contacts.companyName')} *
                  </Label>
                  <Input 
                    data-testid="contact-company" 
                    value={form.company_name} 
                    onChange={e => setForm({...form, company_name: toTitleCase(e.target.value)})} 
                    placeholder={t('contacts.companyName')}
                    className="h-11"
                  />
                </div>
              ) : null}

              {/* Subcontractor-specific fields */}
              {form.account_type === 'subcontractor' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-amber-800">Category</Label>
                    <Select value={form.subcontractor_category} onValueChange={v => setForm({...form, subcontractor_category: v})}>
                      <SelectTrigger data-testid="subcontractor-category" className="bg-white"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {['Driller', 'Pump Installer', 'General Labor'].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-amber-800">Payment Type</Label>
                    <Select value={form.payment_type} onValueChange={v => setForm({...form, payment_type: v})}>
                      <SelectTrigger data-testid="subcontractor-payment-type" className="bg-white"><SelectValue placeholder="Select payment" /></SelectTrigger>
                      <SelectContent>
                        {['Labor', 'Trailer', 'On-site', 'Hourly', 'Per Job'].map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-amber-800">Commission</Label>
                    <Select value={form.commission_type} onValueChange={v => setForm({...form, commission_type: v})}>
                      <SelectTrigger data-testid="subcontractor-commission" className="bg-white"><SelectValue placeholder="Select commission" /></SelectTrigger>
                      <SelectContent>
                        {['100%', 'Fixed Cost', 'Percentage', 'Negotiable'].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    {['commercial', 'subcontractor', 'vendor'].includes(form.account_type)
                      ? t('contacts.contactName') 
                      : t('contacts.firstName')} *
                  </Label>
                  <Input 
                    data-testid="contact-firstname" 
                    value={form.first_name} 
                    onChange={e => setForm({...form, first_name: toTitleCase(e.target.value)})} 
                    placeholder={t('contacts.firstName')} 
                    className="max-w-[220px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('contacts.lastName')}</Label>
                  <Input 
                    data-testid="contact-lastname" 
                    value={form.last_name} 
                    onChange={e => setForm({...form, last_name: toTitleCase(e.target.value)})} 
                    placeholder={t('contacts.lastName')} 
                    className="max-w-[220px]"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                {/* Phone fields */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {t('common.phone')}
                  </Label>
                  <div className="flex gap-2">
                    <Input 
                      data-testid="contact-phone" 
                      value={form.phone} 
                      onChange={e => setForm({...form, phone: formatPhoneNumber(e.target.value)})} 
                      placeholder="(786) 510-4512"
                      className="flex-1 max-w-[280px]"
                    />
                    <Button type="button" variant="outline" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => setForm({...form, additional_phones: [...form.additional_phones, '']})}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {form.additional_phones.map((raw, idx) => {
                    const pe = phoneEntryOf(raw);
                    const update = (field, value) => {
                      const phones = [...form.additional_phones];
                      const next = { ...pe, [field]: value };
                      phones[idx] = (next.name || next.label) ? next : next.phone;
                      setForm({ ...form, additional_phones: phones });
                    };
                    return (
                      <div key={idx} className="flex gap-2">
                        <Input
                          data-testid={`contact-phone-name-${idx + 1}`}
                          value={pe.name}
                          onChange={e => update('name', e.target.value)}
                          placeholder="Name (optional)"
                          className="w-28 flex-shrink-0"
                        />
                        <Input
                          data-testid={`contact-phone-${idx + 1}`}
                          value={pe.phone}
                          onChange={e => update('phone', formatPhoneNumber(e.target.value))}
                          placeholder={`Phone ${idx + 2}`}
                          className="flex-1 max-w-[280px]"
                        />
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0 text-red-400 hover:text-red-600" onClick={() => setForm({...form, additional_phones: form.additional_phones.filter((_, i) => i !== idx)})}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>

                {/* Email fields */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {t('common.email')}
                  </Label>
                  <div className="flex gap-2">
                    <Input 
                      data-testid="contact-email" 
                      value={form.email} 
                      onChange={e => setForm({...form, email: e.target.value})} 
                      placeholder="correo@ejemplo.com"
                      className="flex-1 max-w-[320px]"
                    />
                    <Button type="button" variant="outline" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => setForm({...form, additional_emails: [...form.additional_emails, '']})}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {form.additional_emails.map((raw, idx) => {
                    const ee = emailEntryOf(raw);
                    const update = (field, value) => {
                      const emails = [...form.additional_emails];
                      const next = { ...ee, [field]: value };
                      emails[idx] = next.name ? next : next.email;
                      setForm({ ...form, additional_emails: emails });
                    };
                    return (
                      <div key={idx} className="flex gap-2">
                        <Input
                          data-testid={`contact-email-name-${idx + 1}`}
                          value={ee.name}
                          onChange={e => update('name', e.target.value)}
                          placeholder="Name (optional)"
                          className="w-28 flex-shrink-0"
                        />
                        <Input
                          data-testid={`contact-email-${idx + 1}`}
                          value={ee.email}
                          onChange={e => update('email', e.target.value)}
                          placeholder={`Email ${idx + 2}`}
                          className="flex-1 max-w-[320px]"
                        />
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0 text-red-400 hover:text-red-600" onClick={() => setForm({...form, additional_emails: form.additional_emails.filter((_, i) => i !== idx)})}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Fix F (2026-08-31): Additional Contacts — solo comercial. Alian:
                "tengo dos o tres compañías que son Property Managers, y uno es el
                que paga, uno es el accountant, el otro es el project manager...
                un solo más que me diera el cluster completo". Ningún campo
                interno obligatorio; se oculta (sin borrar datos) si se cambia a
                residencial. */}
            {form.account_type === 'commercial' && (
              <div className="space-y-3" data-testid="additional-contacts-section">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-gray-700">Additional Contacts</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addContactPerson} data-testid="add-contact-person-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Person
                  </Button>
                </div>
                {(form.contact_people || []).map((p, idx) => (
                  <div key={p.id || idx} className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid={`contact-person-card-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 font-medium">Person {idx + 1}</span>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => removeContactPerson(idx)} data-testid={`remove-contact-person-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="First name" value={p.first_name} onChange={e => updateContactPerson(idx, 'first_name', e.target.value)} data-testid={`contact-person-first-name-${idx}`} />
                      <Input placeholder="Last name" value={p.last_name} onChange={e => updateContactPerson(idx, 'last_name', e.target.value)} data-testid={`contact-person-last-name-${idx}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Phone" value={p.phone} onChange={e => updateContactPerson(idx, 'phone', e.target.value)} data-testid={`contact-person-phone-${idx}`} />
                      <Input placeholder="Label (office/cell/...)" value={p.phone_label} onChange={e => updateContactPerson(idx, 'phone_label', e.target.value)} data-testid={`contact-person-phone-label-${idx}`} />
                    </div>
                    <Input placeholder="Email" value={p.email} onChange={e => updateContactPerson(idx, 'email', e.target.value)} data-testid={`contact-person-email-${idx}`} />
                  </div>
                ))}
              </div>
            )}

            {/* Address Section */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                {['commercial', 'subcontractor', 'vendor'].includes(form.account_type)
                  ? t('contacts.companyAddress') 
                  : 'Mailing Address'}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3">
                  <AddressAutocomplete
                    value={form.address}
                    onChange={(addressData) => {
                      const isFullSelection = addressData.lat !== null && addressData.lat !== undefined;
                      setForm(prev => ({
                        ...prev,
                        address: addressData.address || '',
                        // Clear stale city/county/state/zip from a PREVIOUS
                        // selection while the user is still typing a new
                        // address — only trust these fields right after an
                        // actual dropdown pick (isFullSelection). State is
                        // never cleared to '' — it stays FL/whatever it was
                        // so the user doesn't have to re-pick it manually.
                        city: isFullSelection ? (addressData.city || '') : '',
                        county: isFullSelection ? (addressData.county || '') : '',
                        state: isFullSelection ? (stateNameToCode(addressData.state) || prev.state || 'FL') : prev.state,
                        zip: isFullSelection ? (addressData.zip_code || '') : '',
                      }));
                    }}
                    placeholder={t('common.address')}
                  />
                </div>
                <Input value={form.city} onChange={e => setForm({...form, city: toTitleCase(e.target.value)})} placeholder={t('common.city')} />
                <Select value={form.state || 'FL'} onValueChange={v => setForm({...form, state: v, county: v === 'FL' ? form.county : ''})}>
                  <SelectTrigger data-testid="contact-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={form.zip} onChange={e => setForm({...form, zip: e.target.value})} placeholder={t('common.zip')} data-testid="contact-zip-input" />
                <div className="sm:col-span-3">
                  {form.state === 'FL' ? (
                    <Select value={form.county} onValueChange={v => setForm({...form, county: v})}>
                      <SelectTrigger data-testid="contact-county-select"><SelectValue placeholder={t('common.county')} /></SelectTrigger>
                      <SelectContent>
                        {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={form.county}
                      onChange={e => setForm({...form, county: e.target.value})}
                      placeholder={t('common.county')}
                      data-testid="contact-county-input"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('common.notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder={t('common.notes') + '...'} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-contact-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('contacts.saveChanges') : t('contacts.createContact')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Detail Modal */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center text-white text-sm font-bold">
                {selectedContact?.first_name?.[0]}{selectedContact?.last_name?.[0]}
              </div>
              <div>
                <span>{selectedContact?.first_name} {selectedContact?.last_name}</span>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedContact && (
            <div className="space-y-5">
              {/* Quick Actions */}
              <div className="grid grid-cols-3 gap-3">
                <Button 
                  data-testid="create-estimate-from-contact"
                  onClick={() => createEstimate(selectedContact)} 
                  className="flex flex-col items-center gap-2 h-auto py-4 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
                  variant="ghost"
                >
                  <FileText className="w-6 h-6" />
                  <span className="text-xs font-medium">{t('contacts.createEstimate')}</span>
                </Button>
                <Button 
                  data-testid="create-invoice-from-contact"
                  onClick={() => createInvoice(selectedContact)} 
                  className="flex flex-col items-center gap-2 h-auto py-4 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
                  variant="ghost"
                >
                  <Receipt className="w-6 h-6" />
                  <span className="text-xs font-medium">{t('contacts.createInvoice')}</span>
                </Button>
                <Button 
                  data-testid="create-job-from-contact"
                  onClick={() => createJob(selectedContact)} 
                  className="flex flex-col items-center gap-2 h-auto py-4 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200"
                  variant="ghost"
                >
                  <Wrench className="w-6 h-6" />
                  <span className="text-xs font-medium">{t('jobs.newJob')}</span>
                </Button>
              </div>

              {/* Contact Info */}
              <div className="space-y-3 p-4 rounded-lg bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-200 text-slate-700 capitalize">
                    {TYPE_LABELS[selectedContact.type] || selectedContact.type}
                  </span>
                  {selectedContact.company_name && (
                    <span className="flex items-center gap-1 text-sm text-gray-600">
                      <Building className="w-3.5 h-3.5" /> {selectedContact.company_name}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  {selectedContact.phone && (
                    <PhoneLink
                      number={selectedContact.phone}
                      name={[selectedContact.first_name, selectedContact.last_name].filter(Boolean).join(' ') || selectedContact.company_name}
                      testId="contact-detail-phone-link"
                      className="flex gap-2 text-sm !text-gray-700 hover:!text-[#003366]"
                    />
                  )}
                  {selectedContact.email && (
                    <a href={`mailto:${selectedContact.email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#003366] transition-colors">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {selectedContact.email}
                    </a>
                  )}
                </div>

                {(selectedContact.address || selectedContact.city || selectedContact.county) && (
                  <div className="flex items-start gap-2 text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>
                      {[selectedContact.address, selectedContact.city, selectedContact.county, selectedContact.state, selectedContact.zip].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}

                {selectedContact.notes && (
                  <div className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t('common.notes')}</p>
                    {selectedContact.notes}
                  </div>
                )}

                {/* Fix F (2026-08-31): quick-view read-only list of additional people */}
                {selectedContact.account_type === 'commercial' && (selectedContact.contact_people || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200" data-testid="quickview-additional-contacts">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Additional Contacts</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {selectedContact.contact_people.map((p, i) => (
                        <div key={p.id || i} className="text-sm text-gray-700">
                          <p className="font-medium">{[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Contact'}</p>
                          {p.phone && (
                            <PhoneLink number={p.phone} name={[p.first_name, p.last_name].filter(Boolean).join(' ')} className="text-xs !text-gray-600 hover:!text-[#003366]" />
                          )}
                          {p.email && <p className="text-xs text-gray-500">{p.email}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setDetailDialogOpen(false); openEdit(selectedContact); }}>
                  <Pencil className="w-4 h-4 mr-2" /> {t('common.edit')}
                </Button>
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>{t('common.close')}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Addresses Dialog */}
      {manageAddrContact && (
        <ManageAddresses
          open={manageAddrOpen}
          onClose={() => { setManageAddrOpen(false); setManageAddrContact(null); }}
          contactId={manageAddrContact.id}
          contactName={`${manageAddrContact.first_name || ''} ${manageAddrContact.last_name || ''}`.trim() || manageAddrContact.company_name}
          onChanged={fetchData}
        />
      )}
      {/* Import Summary Dialog */}
      <Dialog open={!!importResult} onOpenChange={(o) => { if (!o) setImportResult(null); }}>
        <DialogContent className="max-w-md" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" /> {t('contacts.importSummary')}
            </DialogTitle>
          </DialogHeader>
          {importResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <p className="text-2xl font-bold text-emerald-600" data-testid="import-imported-count">{importResult.imported}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('contacts.imported')}</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-2xl font-bold text-blue-600" data-testid="import-updated-count">{importResult.updated}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('contacts.updated')}</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="text-2xl font-bold text-amber-600" data-testid="import-skipped-count">{importResult.skipped}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('contacts.skipped')}</p>
                </div>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-lg bg-red-50 border border-red-100 p-3">
                  <p className="text-xs font-semibold text-red-700 mb-1">{t('contacts.importErrors')}</p>
                  <ul className="text-xs text-red-600 space-y-0.5 list-disc list-inside">
                    {importResult.errors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => setImportResult(null)} className="bg-[#003366] hover:bg-[#004080] text-white">{t('common.close')}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
